/* ==========================================================================
   Pila — Klaviyo signup relay
   One submit path for every email form on the site (footer newsletter, PDP
   launch list, product notify form, international interest form).

   Why: posting from the browser straight to a.klaviyo.com fails for a big
   slice of visitors — Firefox's Enhanced Tracking Protection blocks that
   domain out of the box, and so does every ad blocker. So the primary path
   is our own API (first-party, never on a blocklist), which relays to
   Klaviyo server-side. Direct-to-Klaviyo remains only as a fallback for
   when the API itself is unreachable.

   Usage:
     PilaSubscribe({ email, listId, source, companyId,
                     countryCode?, region?, note?, apiBase? })
       .then(onSuccess, function (err) { show(err.message) })
   ========================================================================== */

(function () {
  'use strict';
  if (window.PilaSubscribe) return; // loaded once per page, guarded per section

  var DEFAULT_API = 'https://pila-shopify.vercel.app';
  var REVISION = '2024-02-15';

  function countryName(code) {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
    } catch (e) {
      return code;
    }
  }

  // The old client-side call, kept as the fallback. Mirrors what the API
  // builds server-side so the profile looks identical either way.
  function direct(opts) {
    var attrs = { email: opts.email };
    if (opts.countryCode) {
      attrs.location = {
        country: countryName(opts.countryCode),
        region: opts.region || null
      };
      attrs.properties = {
        'Interest Country Code': opts.countryCode,
        'Interest Note': opts.note || null,
        'Signup Surface': opts.surface || opts.source
      };
    }
    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' +
        encodeURIComponent(opts.companyId),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: opts.source,
              profile: { data: { type: 'profile', attributes: attrs } }
            },
            relationships: { list: { data: { type: 'list', id: opts.listId } } }
          }
        })
      }
    ).then(function (res) {
      if (res.ok || res.status === 202) return true;
      throw new Error('Something went wrong. Please try again.');
    });
  }

  window.PilaSubscribe = function (opts) {
    var base = (opts.apiBase || DEFAULT_API).replace(/\/$/, '');

    return fetch(base + '/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: opts.email,
        listId: opts.listId,
        source: opts.source,
        countryCode: opts.countryCode || null,
        region: opts.region || null,
        note: opts.note || null,
        surface: opts.surface || null
      })
    }).then(function (res) {
      if (res.ok) return true;
      // The API answered but said no (bad email, rate limit, Klaviyo down).
      // Going direct would hit the same wall, so surface its message instead.
      return res.json().catch(function () { return {}; }).then(function (body) {
        throw new Error(body.error || 'Something went wrong. Please try again.');
      });
    }, function () {
      // The API itself was unreachable — try the old direct path so an API
      // outage doesn't take signups down for visitors who aren't blocked.
      return direct(opts);
    });
  };
})();
